/**
 * The ONE per-field confidence chip, and the ONE palette behind it.
 *
 * Extracted from `JsonSchemaForm` so the decision surface can present
 * confidence the SAME way the launch form already does. A second inline copy
 * would drift — the launch form and the decision card would disagree about
 * what "medium" looks like, on the one signal whose whole job is to be read at
 * a glance.
 *
 * WHERE A CHIP BELONGS. Only on a field the model INFERRED from the email.
 * Once the client and policy are resolved, the remaining values are read from
 * the Level 2 Databricks tables — those are lookups, not inferences, and
 * decorating them would imply a confidence that does not exist. So:
 *
 *   chip     — effective_date, change_type, the new-mortgagee fields, and
 *              target_interest (naming the old mortgagee IS an inference:
 *              which existing Epic record the email means)
 *   no chip  — policy_number, policy_type_code, term_effective_date,
 *              term_expiration_date, and the client/policy context
 *
 * `_PREFILL_ACTION_KEYS` in the substrate mixes both kinds in one tuple with no
 * marker, so that split cannot be recovered from the code — hence writing it
 * down here, next to the thing that renders it.
 */
import { useId, type ReactElement } from "react";
import CustomTooltip from "~/common/components/CustomTooltip";

export type ConfidenceLevel = "high" | "medium" | "low";

export const CONFIDENCE_COLORS: Record<
  ConfidenceLevel,
  { border: string; text: string; label: string; dot: string }
> = {
  // primary-success-600 / primary-warning-500 / primary-error-600
  high:   { border: "#079455", text: "text-primary-success-600", label: "High",   dot: "bg-primary-success-600" },
  medium: { border: "#F79009", text: "text-primary-warning-500", label: "Medium", dot: "bg-primary-warning-500" },
  low:    { border: "#D92D20", text: "text-primary-error-600",   label: "Low",    dot: "bg-primary-error-600"   },
};

/** Weakest-first, so a roll-up can take the minimum of several components. */
const CONFIDENCE_RANK: Record<ConfidenceLevel, number> = {
  low: 0,
  medium: 1,
  high: 2,
};

/**
 * The weakest level among several — used where the decision surface shows ONE
 * control for what the launch form splits into components.
 *
 * The decision panel's mortgagee record carries a single `mailing_address`
 * textarea, while the substrate scores street / city / state / zip separately.
 * Rolling up to the weakest is the conservative read: the chip exists to tell
 * the Servicer what deserves a second look, and "one part of this address was a
 * guess" is exactly that. Averaging, or taking the best, would hide it.
 *
 * Returns undefined when nothing was scored, so no chip renders at all.
 */
export function weakestConfidence(
  levels: (ConfidenceLevel | undefined)[],
): ConfidenceLevel | undefined {
  const present = levels.filter((l): l is ConfidenceLevel => l !== undefined);
  if (present.length === 0) return undefined;
  return present.reduce((a, b) => (CONFIDENCE_RANK[b] < CONFIDENCE_RANK[a] ? b : a));
}

/**
 * The chip itself: a coloured dot plus the level (so the level is readable
 * without relying on colour alone), with "<Level> confidence" in ora's
 * tooltip (story 28633).
 */
export function ConfidenceChip({
  confidence,
}: {
  confidence?: ConfidenceLevel;
}): JSX.Element | null {
  const conf = confidence ? CONFIDENCE_COLORS[confidence] : null;
  if (!conf) return null;
  return (
    <ConfidenceTooltip label={conf.label}>
      <span
        className={`inline-flex items-center gap-[3px] text-2xs font-medium ${conf.text}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${conf.dot}`} />
        {conf.label}
      </span>
    </ConfidenceTooltip>
  );
}

/**
 * "<Level> confidence" in ora's CustomTooltip around a confidence chip — the
 * chip here and the inline copies in the launch form's labels.
 *
 * `describeChild` with a NODE title: the chip usually sits inside a field's
 * <label>, whose descendants make up the field's accessible name, so the
 * tooltip must not become the chip's aria-label; and a string title under
 * `describeChild` would put a native `title` back on the chip while closed.
 */
export function ConfidenceTooltip({
  label,
  children,
}: {
  label: string;
  children: ReactElement;
}): JSX.Element {
  const id = `confidence-${useId().replace(/:/g, "")}`;
  return (
    <CustomTooltip
      id={id}
      title={<span>{`${label} confidence`}</span>}
      describeChild
      placement="top"
      arrow
      isOnModal
    >
      {children}
    </CustomTooltip>
  );
}

/** The matching coloured left border, for the control the chip labels. */
export function confidenceBorderStyle(
  confidence?: ConfidenceLevel,
): { borderLeftColor: string; borderLeftWidth: string } | undefined {
  if (!confidence) return undefined;
  return {
    borderLeftColor: CONFIDENCE_COLORS[confidence].border,
    borderLeftWidth: "3px",
  };
}

/**
 * The fields ora INFERRED from the email — the only ones that may carry a
 * chip. The prose at the top of this file has always said which those are;
 * this is that sentence, executable (story 27442).
 *
 * `mortgagees.*` is a prefix because the substrate scores the extracted
 * mortgagee per sub-field (`mortgagees.name`, `mortgagees.street`, …) and new
 * sub-fields must inherit the answer rather than silently lose their chip.
 *
 * Everything absent from this list is a Level 2 lookup — policy number, policy
 * type, term dates, the client and policy context. Decorating one of those
 * would imply a confidence that does not exist: nothing scored it, so the chip
 * would be describing an inference that never happened.
 */
export const INFERRED_FIELD_KEYS: readonly string[] = [
  "effective_date",
  "change_type",
  "target_interest",
];

/** Prefix form, for the per-sub-field mortgagee addresses. */
export const INFERRED_FIELD_PREFIXES: readonly string[] = ["mortgagees."];

/** True when a chip belongs on this field. */
export function isInferredField(key: string): boolean {
  return (
    INFERRED_FIELD_KEYS.includes(key) ||
    INFERRED_FIELD_PREFIXES.some((p) => key.startsWith(p))
  );
}

/**
 * The inferred subset of a confidence map, or `undefined` when nothing in it
 * qualifies — so a surface handed a map full of lookup fields renders no chips
 * at all rather than an empty decoration.
 */
export function inferredOnly(
  fieldConfidence?: Record<string, ConfidenceLevel>,
): Record<string, ConfidenceLevel> | undefined {
  if (!fieldConfidence) return undefined;
  const out: Record<string, ConfidenceLevel> = {};
  for (const [key, level] of Object.entries(fieldConfidence)) {
    if (isInferredField(key)) out[key] = level;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}
